import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, TextInput, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { OverlayFooter, OverlayHeader, OverlayPanel } from './OverlayPanel';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { DateRangePicker, DateRangePreset } from './DateRangePicker';
import { getChannelIcon, getChannelColor } from '../utils/channelIcons';

const providerNameKey = (provider: string): string => {
  switch (provider) {
    case 'whatsapp': return 'contacts.provider.whatsapp';
    case 'facebook-messenger': return 'contacts.provider.messenger';
    case 'x-twitter': return 'contacts.provider.xTwitter';
    case 'phone': return 'contacts.provider.phone';
    case 'telegram': return 'contacts.provider.telegram';
    case 'email': return 'contacts.provider.email';
    default: return '';
  }
};

/**
 * Real, parameterized analytical filters for Contacts — same interaction
 * pattern as the Reports screen's own filters (a real date-range calendar
 * picker, a typed numeric threshold), not fixed preset chips with a
 * hardcoded threshold baked into the backend. Explicitly modeled on that
 * screen's DateRangePicker (reused directly here) per direct feedback that
 * a static "Follow-up due (3+ days)" chip wasn't a real analytical filter.
 * Lives in its own modal (not a row on the main Contacts screen) so it
 * doesn't add to an already dense toolbar.
 */
export interface ContactAnalyticalFilters {
  needsResponse?: boolean;
  expiringSoon?: boolean;
  // Zero linked channels at all — same "hasChannels: no value" signal the
  // Home widget's neverContacted row and the Sort & Filter modal's
  // "Contacted (any channel)" field already use.
  neverContacted?: boolean;
  // Contact hasn't replied to us in at least this many days — undefined/0
  // means the filter is off. Generalizes what was a fixed 3-day constant.
  noReplyDays?: number;
  orderDatePreset?: DateRangePreset;
  orderDateFrom?: Date;
  orderDateTo?: Date;
  // true = has an order in the range above; false = has NO order in the
  // range (a churn/lapsed-customer filter); undefined = date range not
  // applied as a filter yet (still just being picked).
  hasOrderInRange?: boolean;
  // "Contacted" via a specific channel (undefined = any channel) within
  // [contactedDateFrom, contactedDateTo] — "contacted" means that
  // channel's most recent message falls in the range (a "last contacted"
  // definition; see OmniChannelService.getContactedContactIds).
  contactedChannel?: string;
  contactedDatePreset?: DateRangePreset;
  contactedDateFrom?: Date;
  contactedDateTo?: Date;
  hasContactedInRange?: boolean;
  // "Contacted us back" at least this many times in total, across every
  // channel — undefined/0 means the filter is off.
  minMessageCount?: number;
}

export const isAnalyticalFiltersEmpty = (filters: ContactAnalyticalFilters): boolean => {
  return !filters.needsResponse
    && !filters.expiringSoon
    && !filters.neverContacted
    && !filters.noReplyDays
    && filters.hasOrderInRange === undefined
    && filters.hasContactedInRange === undefined
    && !filters.minMessageCount;
};

export const countActiveAnalyticalFilters = (filters: ContactAnalyticalFilters): number => {
  let count = 0;
  if (filters.needsResponse) count++;
  if (filters.expiringSoon) count++;
  if (filters.neverContacted) count++;
  if (filters.noReplyDays) count++;
  if (filters.hasOrderInRange !== undefined) count++;
  if (filters.hasContactedInRange !== undefined) count++;
  if (filters.minMessageCount) count++;
  return count;
};

interface Props {
  visible: boolean;
  value: ContactAnalyticalFilters;
  onApply: (next: ContactAnalyticalFilters) => void;
  onClose: () => void;
  // Channels available to filter "contacted" by — same list the Channel
  // filter itself offers (ContactsList's `providers`), so the two stay
  // consistent.
  availableChannels: string[];
}

export const ContactAnalyticalFiltersModal: React.FC<Props> = ({ visible, value, onApply, onClose, availableChannels }) => {
  const styles = useThemedStyles(getStyles);
  const insets = useSafeAreaInsets();
  const { t } = useLocale();
  const rtl = useRTLStyles();

  const [draft, setDraft] = useState<ContactAnalyticalFilters>(value);
  const [noReplyDaysText, setNoReplyDaysText] = useState<string>(value.noReplyDays ? String(value.noReplyDays) : '');
  const [minMessageCountText, setMinMessageCountText] = useState<string>(value.minMessageCount ? String(value.minMessageCount) : '');

  // Re-sync the draft to the caller's committed value each time the modal
  // opens, so a cancelled edit never leaks into the next open.
  useEffect(() => {
    if (visible) {
      setDraft(value);
      setNoReplyDaysText(value.noReplyDays ? String(value.noReplyDays) : '');
      setMinMessageCountText(value.minMessageCount ? String(value.minMessageCount) : '');
    }
  }, [visible, value]);

  const handleNoReplyDaysChange = (text: string) => {
    const digitsOnly = text.replace(/[^0-9]/g, '');
    setNoReplyDaysText(digitsOnly);
    const parsed = parseInt(digitsOnly, 10);
    setDraft((prev) => ({ ...prev, noReplyDays: Number.isFinite(parsed) && parsed > 0 ? parsed : undefined }));
  };

  const handleMinMessageCountChange = (text: string) => {
    const digitsOnly = text.replace(/[^0-9]/g, '');
    setMinMessageCountText(digitsOnly);
    const parsed = parseInt(digitsOnly, 10);
    setDraft((prev) => ({ ...prev, minMessageCount: Number.isFinite(parsed) && parsed > 0 ? parsed : undefined }));
  };

  const handleDateRangeChange = (preset: DateRangePreset, startDate?: Date, endDate?: Date) => {
    setDraft((prev) => ({
      ...prev,
      orderDatePreset: preset,
      orderDateFrom: startDate,
      orderDateTo: endDate,
      // Picking/changing a range keeps whichever has/hasn't-ordered mode
      // was already selected, defaulting to "has an order" the first time
      // a range is chosen at all.
      hasOrderInRange: prev.hasOrderInRange ?? true,
    }));
  };

  const handleContactedDateRangeChange = (preset: DateRangePreset, startDate?: Date, endDate?: Date) => {
    setDraft((prev) => ({
      ...prev,
      contactedDatePreset: preset,
      contactedDateFrom: startDate,
      contactedDateTo: endDate,
      hasContactedInRange: prev.hasContactedInRange ?? true,
    }));
  };

  const activeCount = countActiveAnalyticalFilters(draft);

  const handleClear = () => {
    setDraft({});
    setNoReplyDaysText('');
    setMinMessageCountText('');
  };

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  return (
    <OverlayPanel visible={visible} onClose={onClose}>
      {/* Full-screen page inside a transparent Modal — React Native adds no
          safe-area padding, so keep the header clear of the notch/status
          bar and the footer clear of the home indicator. */}
      <View style={[styles.page, { paddingTop: insets.top, paddingBottom: insets.bottom }]}>
        <OverlayHeader icon="analytics-outline" title={t('contacts.analyticalFilters.title')} onClose={onClose} right={(
          <TouchableOpacity onPress={handleClear} disabled={activeCount === 0} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Text style={[styles.resetText, activeCount === 0 && styles.resetTextDisabled]}>{t('contacts.sort.clearSort')}</Text>
          </TouchableOpacity>
        )} />

        <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent} keyboardShouldPersistTaps="handled">
          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('contacts.analyticalFilters.responseSection')}</Text>
            <Text style={[styles.sectionHint, { textAlign: rtl.textStart }]}>{t('contacts.analyticalFilters.responseHint')}</Text>
            <View style={[styles.chipsRow, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity
                style={[styles.presenceChip, draft.needsResponse && styles.presenceChipActive]}
                onPress={() => setDraft((prev) => ({ ...prev, needsResponse: !prev.needsResponse }))}
              >
                <Text style={[styles.presenceChipText, draft.needsResponse && styles.presenceChipTextActive]}>
                  {t('outreachGuidance.needsResponse.title')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.presenceChip, draft.expiringSoon && styles.presenceChipActive]}
                onPress={() => setDraft((prev) => ({ ...prev, expiringSoon: !prev.expiringSoon }))}
              >
                <Text style={[styles.presenceChipText, draft.expiringSoon && styles.presenceChipTextActive]}>
                  {t('outreachGuidance.expiringSoon.title')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.presenceChip, draft.neverContacted && styles.presenceChipActive]}
                onPress={() => setDraft((prev) => ({ ...prev, neverContacted: !prev.neverContacted }))}
              >
                <Text style={[styles.presenceChipText, draft.neverContacted && styles.presenceChipTextActive]}>
                  {t('outreachGuidance.neverContacted.title')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('contacts.analyticalFilters.noReplySection')}</Text>
            <Text style={[styles.sectionHint, { textAlign: rtl.textStart }]}>{t('contacts.analyticalFilters.noReplyHint')}</Text>
            <View style={[styles.daysInputRow, { flexDirection: rtl.rowDirection }]}>
              <Text style={styles.daysInputLabel}>{t('contacts.analyticalFilters.noReplyPrefix')}</Text>
              <TextInput
                style={styles.daysInput}
                value={noReplyDaysText}
                onChangeText={handleNoReplyDaysChange}
                keyboardType="number-pad"
                placeholder="3"
                placeholderTextColor={colors.text.disabled as string}
                maxLength={3}
              />
              <Text style={styles.daysInputLabel}>{t('contacts.analyticalFilters.noReplySuffix')}</Text>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('contacts.analyticalFilters.orderSection')}</Text>
            <Text style={[styles.sectionHint, { textAlign: rtl.textStart }]}>{t('contacts.analyticalFilters.orderHint')}</Text>
            {/* Has/hasn't works standalone ("ever placed an order at all")
                — a date range is optional extra scoping, not required. */}
            <View style={[styles.chipsRow, { flexDirection: rtl.rowDirection, marginBottom: spacing.sm }]}>
              <TouchableOpacity
                style={[styles.presenceChip, draft.hasOrderInRange === true && styles.presenceChipActive]}
                onPress={() => setDraft((prev) => ({ ...prev, hasOrderInRange: prev.hasOrderInRange === true ? undefined : true }))}
              >
                <Text style={[styles.presenceChipText, draft.hasOrderInRange === true && styles.presenceChipTextActive]}>
                  {t('contacts.analyticalFilters.hasOrder')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.presenceChip, draft.hasOrderInRange === false && styles.presenceChipActive]}
                onPress={() => setDraft((prev) => ({ ...prev, hasOrderInRange: prev.hasOrderInRange === false ? undefined : false }))}
              >
                <Text style={[styles.presenceChipText, draft.hasOrderInRange === false && styles.presenceChipTextActive]}>
                  {t('contacts.analyticalFilters.noOrder')}
                </Text>
              </TouchableOpacity>
            </View>
            <Text style={[styles.sectionHint, { textAlign: rtl.textStart }]}>{t('contacts.analyticalFilters.orderDateHint')}</Text>
            <DateRangePicker
              preset={draft.orderDatePreset || 'custom'}
              startDate={draft.orderDateFrom}
              endDate={draft.orderDateTo}
              onChange={handleDateRangeChange}
            />
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('contacts.analyticalFilters.returnedMultipleSection')}</Text>
            <Text style={[styles.sectionHint, { textAlign: rtl.textStart }]}>{t('contacts.analyticalFilters.returnedMultipleHint')}</Text>
            <View style={[styles.daysInputRow, { flexDirection: rtl.rowDirection }]}>
              <Text style={styles.daysInputLabel}>{t('contacts.analyticalFilters.returnedMultiplePrefix')}</Text>
              <TextInput
                style={styles.daysInput}
                value={minMessageCountText}
                onChangeText={handleMinMessageCountChange}
                keyboardType="number-pad"
                placeholder="3"
                placeholderTextColor={colors.text.disabled as string}
                maxLength={4}
              />
              <Text style={styles.daysInputLabel}>{t('contacts.analyticalFilters.returnedMultipleSuffix')}</Text>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('contacts.analyticalFilters.contactedSection')}</Text>
            <Text style={[styles.sectionHint, { textAlign: rtl.textStart }]}>{t('contacts.analyticalFilters.contactedHint')}</Text>
            <View style={[styles.chipsRow, { flexDirection: rtl.rowDirection, marginBottom: spacing.sm }]}>
              <TouchableOpacity
                style={[styles.presenceChip, { flexDirection: rtl.rowDirection }, !draft.contactedChannel && styles.presenceChipActive]}
                onPress={() => setDraft((prev) => ({ ...prev, contactedChannel: undefined }))}
              >
                <Ionicons name="apps-outline" size={14} color={!draft.contactedChannel ? colors.white : colors.text.primary as string} />
                <Text style={[styles.presenceChipText, !draft.contactedChannel && styles.presenceChipTextActive]}>
                  {t('contacts.filter.allChannels')}
                </Text>
              </TouchableOpacity>
              {availableChannels.map((channel) => {
                const isActive = draft.contactedChannel === channel;
                const nameKey = providerNameKey(channel);
                return (
                  <TouchableOpacity
                    key={channel}
                    style={[styles.presenceChip, { flexDirection: rtl.rowDirection }, isActive && styles.presenceChipActive]}
                    onPress={() => setDraft((prev) => ({ ...prev, contactedChannel: channel }))}
                  >
                    <Ionicons name={getChannelIcon(channel) as any} size={14} color={isActive ? colors.white : getChannelColor(channel)} />
                    <Text style={[styles.presenceChipText, isActive && styles.presenceChipTextActive]}>
                      {nameKey ? t(nameKey as any) : channel}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            <DateRangePicker
              preset={draft.contactedDatePreset || 'custom'}
              startDate={draft.contactedDateFrom}
              endDate={draft.contactedDateTo}
              onChange={handleContactedDateRangeChange}
            />
            {draft.contactedDateFrom && draft.contactedDateTo && (
              <View style={[styles.chipsRow, { flexDirection: rtl.rowDirection, marginTop: spacing.sm }]}>
                <TouchableOpacity
                  style={[styles.presenceChip, draft.hasContactedInRange === true && styles.presenceChipActive]}
                  onPress={() => setDraft((prev) => ({ ...prev, hasContactedInRange: true }))}
                >
                  <Text style={[styles.presenceChipText, draft.hasContactedInRange === true && styles.presenceChipTextActive]}>
                    {t('contacts.analyticalFilters.wasContacted')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.presenceChip, draft.hasContactedInRange === false && styles.presenceChipActive]}
                  onPress={() => setDraft((prev) => ({ ...prev, hasContactedInRange: false }))}
                >
                  <Text style={[styles.presenceChipText, draft.hasContactedInRange === false && styles.presenceChipTextActive]}>
                    {t('contacts.analyticalFilters.wasNotContacted')}
                  </Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </ScrollView>

        <OverlayFooter>
          <TouchableOpacity style={styles.applyButton} onPress={handleApply}>
            <Text style={styles.applyButtonText}>
              {activeCount > 0 ? t('contacts.analyticalFilters.applyWithCount', { count: activeCount }) : t('common.apply')}
            </Text>
          </TouchableOpacity>
        </OverlayFooter>
      </View>
    </OverlayPanel>
  );
};

const getStyles = () => StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: colors.background.surface,
    ...(Platform.OS === 'web' && { maxWidth: 640, alignSelf: 'center' as const, width: '100%' as const, backgroundColor: colors.background.surfaceRaised }),
  },
  header: {
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerTitle: {
    flex: 1,
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.text.primary,
  },
  resetText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: '600',
  },
  resetTextDisabled: {
    color: colors.text.disabled,
  },
  body: {
    flex: 1,
    marginTop: spacing.md,
  },
  bodyContent: {
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.xl,
  },
  section: {
    marginBottom: spacing.lg,
  },
  sectionTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: '700',
    color: colors.text.secondary,
    marginBottom: spacing.xs,
  },
  sectionHint: {
    fontSize: typography.fontSize.xs,
    color: colors.text.disabled,
    marginBottom: spacing.sm,
  },
  chipsRow: {
    gap: spacing.sm,
    flexWrap: 'wrap',
  },
  presenceChip: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  presenceChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  presenceChipText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontWeight: '600',
  },
  presenceChipTextActive: {
    color: colors.white,
  },
  daysInputRow: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  daysInputLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
  },
  daysInput: {
    width: 56,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.sm,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    textAlign: 'center',
    backgroundColor: colors.background.surfaceRaised,
  },
  footer: {
    padding: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  applyButton: {
    backgroundColor: colors.primary,
    borderRadius: spacing.borderRadius.md,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  applyButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.base,
    fontWeight: '700',
  },
});
